import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Card, Spinner, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import PropTypes from 'prop-types';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';
import ModalAgregarProductoSuscripcion from '../suscripciones/Modales/ModalAgregarProductoSuscripcion';

const SeccionModulosLicenciaCliente = ({ idLicencia, infoSuscripcion }) => {
    const { token } = useToken();
    const { showErrorToast } = useToast();

    const [modulos, setModulos] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [showModal, setShowModal] = useState(false);

    // Carga los módulos asociados a la licencia
    const cargarModulos = useCallback(async () => {
        if (!idLicencia) return;
        setCargando(true);
        try {
            const resp = await fetch(`/api/licencia/ObtenerModulosPorLicencia?idLicencia=${idLicencia}`, {
                method: 'GET',
                cache: 'no-store',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            });
            const data = await resp.json();
            if (data.iResponseType === 1 && data.detalles) {
                const det = JSON.parse(data.detalles);
                setModulos(Array.isArray(det?.table2) ? det.table2 : []);
            } else {
                setModulos([]);
            }
        } catch {
            showErrorToast('Error al cargar módulos de la licencia');
        } finally {
            setCargando(false);
        }
    }, [idLicencia, token]);

    useEffect(() => {
        cargarModulos();
    }, [cargarModulos]);

    return (
        <>
            <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                        <FontAwesomeIcon
                            icon={icon({ name: 'puzzle-piece', style: 'solid' })}
                            className="me-2 text-primary"
                        />
                        Módulos de Suscripción
                    </h5>
                    {infoSuscripcion && (
                        <Button variant="primary" size="sm" onClick={() => setShowModal(true)}>
                            <FontAwesomeIcon icon={icon({ name: 'plus', style: 'solid' })} className="me-1" />
                            Agregar Módulo
                        </Button>
                    )}
                </Card.Header>

                <Card.Body className="p-0">
                    {cargando ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" variant="primary" />
                        </div>
                    ) : (
                        <Table responsive hover size="sm" className="mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th>#</th>
                                    <th>Módulo</th>
                                    <th>Producto</th>
                                    <th>Renovación</th>
                                    <th>Total</th>
                                    <th>Estado</th>
                                    <th>Asignado por</th>
                                </tr>
                            </thead>
                            <tbody>
                                {modulos.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className="text-center text-muted py-4">
                                            Sin módulos asignados.
                                        </td>
                                    </tr>
                                ) : (
                                    modulos.map(m => (
                                        <tr key={m.idModulosLicenciaCliente}>
                                            <td>{m.idModulosLicenciaCliente}</td>
                                            <td>{m.vDescripcionModulo || '—'}</td>
                                            <td>{m.vNombreProducto}</td>
                                            <td>{m.vDescripcion}</td>
                                            <td>
                                                ${m.dTotal?.toFixed(2)}{' '}
                                                <span className="text-muted small">{m.vMoneda}</span>
                                            </td>
                                            <td>
                                                <Badge bg={m.bIsActive ? 'success' : 'secondary'}>
                                                    {m.bIsActive ? 'Activo' : 'Inactivo'}
                                                </Badge>
                                            </td>
                                            <td className="text-muted small">
                                                {m.vCreatedUser}
                                                {m.dCreatedDate
                                                    ? ` · ${new Date(m.dCreatedDate).toLocaleDateString()}`
                                                    : ''}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>
            </Card>

            <ModalAgregarProductoSuscripcion
                show={showModal}
                onHide={() => setShowModal(false)}
                suscripcion={infoSuscripcion ? { ...infoSuscripcion, idLicencia: infoSuscripcion.idLicencia ?? idLicencia } : null}
                onCreated={cargarModulos}
            />
        </>
    );
};

SeccionModulosLicenciaCliente.propTypes = {
    idLicencia: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    idClienteAkasia: PropTypes.number,
    infoSuscripcion: PropTypes.object
};

export default SeccionModulosLicenciaCliente;
